// 403
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Key,
  Mail,
  Phone,
  Building,
  CheckCircle,
  Copy,
  Eye,
  EyeOff,
  XCircle,
} from "lucide-react";
import Layout from "./Layout";
import ManualDeletionGuide from "./ManualDeletionGuide";
import { useAuth } from "../context/AuthContext";
import { doc, setDoc, getDoc, updateDoc, deleteDoc, collection, getDocs } from "firebase/firestore";
import { createUserWithEmailAndPassword, signOut, signInWithEmailAndPassword } from "firebase/auth";
import { db, clientAuth } from "../firebase/config";
import {
  Modal,
  Button,
  Spinner,
  Toast,
  ToastContainer,
  InputGroup,
  Form,
} from "react-bootstrap";
import "./ClientManagement.css";

// --- Helper Component for Skeleton Loading ---
const SkeletonRow = () => (
  <tr>
    <td colSpan="5">
      <div className="d-flex align-items-center p-2">
        <div
          className="skeleton-box me-3"
          style={{ width: "40px", height: "40px", borderRadius: "50%" }}
        ></div>
        <div style={{ flex: 1 }}>
          <div
            className="skeleton-box mb-2"
            style={{ height: "1rem", width: "40%" }}
          ></div>
          <div
            className="skeleton-box"
            style={{ height: "0.8rem", width: "80%" }}
          ></div>
        </div>
      </div>
    </td>
  </tr>
);

const ClientManagement = () => {
  const {
    isAuthenticated,
    loading: authLoading,
    deleteUserAccount,
    updateUserPassword,
    user,
  } = useAuth();
  const navigate = useNavigate();

  // Helper function to sanitize email for use as Firebase key
  const sanitizeEmail = (email) => {
    return email.replace(/[.#$[\]]/g, "_");
  };

  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [showManualDeletionGuide, setShowManualDeletionGuide] = useState(false);
  const [deletedUserEmail, setDeletedUserEmail] = useState("");
  const [toastInfo, setToastInfo] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firmName: "",
    contactPerson: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    status: "active", // Added for the new status feature
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // --- Data and Auth Logic ---
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    loadClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const filtered = clients.filter(
      (client) =>
        (client.firmName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.contactPerson || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClients(filtered);
  }, [clients, searchTerm]);

  const showToast = useCallback((message, variant = "success") => {
    setToastInfo({ show: true, message, variant });
  }, []);

  const loadClients = useCallback(async () => {
    setDataLoading(true);
    try {
      const clientsRef = collection(db, "ca_admin");
      const snapshot = await getDocs(clientsRef);
      const clientsArray = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Only include documents that are CA firms (not admin users)
        if (data.firmName) {
          clientsArray.push({
            id: doc.id,
            ...data,
          });
        }
      });
      setClients(clientsArray);
      localStorage.setItem("caClients", JSON.stringify(clientsArray));
    } catch (error) {
      console.error("Error loading clients:", error);
      showToast("Could not load client data.", "danger");
    } finally {
      setDataLoading(false);
    }
  }, [showToast]);

  // Individual Field Validation
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "firmName":
        if (!value.trim()) {
          error = "Firm name is required";
        } else if (value.trim().length < 2) {
          error = "Firm name must be at least 2 characters";
        }
        break;

      case "contactPerson":
        const nameRegex = /^[a-zA-Z\s.]+$/;
        if (!value.trim()) {
          error = "Contact person is required";
        } else if (value.trim().length < 2) {
          error = "Contact person name must be at least 2 characters";
        } else if (!nameRegex.test(value)) {
          error = "Name should only contain letters and dots (.)";
        }
        break;

      case "email":
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!value.trim()) {
          error = "Email is required";
        } else if (!emailRegex.test(value)) {
          error = "Please enter a valid email address (e.g., user@example.com)";
        }
        break;

      case "phone":
        const cleanPhone = value.replace(/[^0-9]/g, "");
        if (!value.trim()) {
          error = "Phone number is required";
        } else if (cleanPhone.length !== 10) {
          error = "Phone number must have exactly 10 digits";
        }
        break;

      case "address":
        if (!value.trim()) {
          error = "Address is required";
        } else if (value.trim().length < 10) {
          error = "Please provide a complete address (minimum 10 characters)";
        }
        break;

      case "password":
        if (!editingClient) {
          if (!value) {
            error = "Password is required";
          } else if (value.length < 6) {
            error = "Password must be at least 6 characters";
          } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
            error = "Password must contain at least one uppercase letter, one lowercase letter, and one number";
          }
        } else if (value && value.length < 6) {
          error = "Password must be at least 6 characters";
        }
        break;

      default:
        break;
    }

    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Form validation function
  const validateForm = () => {
    const errors = {};

    // Firm Name validation
    if (!formData.firmName.trim()) {
      errors.firmName = "Firm name is required";
    } else if (formData.firmName.trim().length < 2) {
      errors.firmName = "Firm name must be at least 2 characters";
    }

    // Contact Person validation (Alphabets, spaces, and dots allowed)
    const nameRegex = /^[a-zA-Z\s.]+$/;
    if (!formData.contactPerson.trim()) {
      errors.contactPerson = "Contact person is required";
    } else if (formData.contactPerson.trim().length < 2) {
      errors.contactPerson = "Contact person name must be at least 2 characters";
    } else if (!nameRegex.test(formData.contactPerson)) {
      errors.contactPerson = "Name should only contain letters and dots (.)";
    }

    // Email validation (Strict format)
    // Checks for: characters@domain.extension (where extension is 2+ chars)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address (e.g., user@example.com)";
    }

    // Phone validation (Check for exactly 10 digits, ignoring formatting)
    const cleanPhone = formData.phone.replace(/[^0-9]/g, "");
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (cleanPhone.length !== 10) {
      // जर आकड्यांची संख्या 10 नसेल (उदा. 9 किंवा 11), तर एरर देईल
      errors.phone = "Phone number must have exactly 10 digits";
    }

    // Address validation
    if (!formData.address.trim()) {
      errors.address = "Address is required";
    } else if (formData.address.trim().length < 10) {
      errors.address = "Please provide a complete address (minimum 10 characters)";
    }

    // Password validation (only for new firms)
    if (!editingClient) {
      if (!formData.password) {
        errors.password = "Password is required";
      } else if (formData.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
        errors.password =
          "Password must contain at least one uppercase letter, one lowercase letter, and one number";
      }
    } else if (formData.password && formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      showToast(
        "Please fix the validation errors before submitting.",
        "danger"
      );
      return;
    }

    setIsSubmitting(true);
    try {

      if (!editingClient) {
        const sanitizedId = sanitizeEmail(formData.email);
        if (clients.find((client) => client.id === sanitizedId)) {
          showToast("A firm with this email already exists.", "danger");
          return;
        }

        let userCredential;
        let isExistingUser = false;

        try {
          // Use separate auth instance to create user without affecting admin session
          userCredential = await createUserWithEmailAndPassword(
            clientAuth,
            formData.email,
            formData.password
          );
        } catch (authError) {
          if (authError.code === "auth/email-already-in-use") {
            // If user already exists, try to sign in to verify credentials and link
            try {
              userCredential = await signInWithEmailAndPassword(
                clientAuth,
                formData.email,
                formData.password
              );
              isExistingUser = true;
            } catch (signInError) {
              // If password doesn't match, we have a stale user problem
              setDeletedUserEmail(formData.email);
              setShowManualDeletionGuide(true);
              throw new Error(
                "User already exists in Authentication but not in Database. Please delete the user manually from Firebase Console to proceed."
              );
            }
          } else {
            throw authError; // Rethrow other errors
          }
        }

        // Immediately sign out from the client auth instance to prevent session conflicts
        await signOut(clientAuth);

        const clientData = {
          uid: userCredential.user.uid,
          ...formData,
          credentials: {
            email: formData.email,
            password: formData.password,
          },
          status: "active",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const clientRef = doc(db, "ca_admin", sanitizedId);
        await setDoc(clientRef, clientData);
        setClients([...clients, { id: sanitizedId, ...clientData }]);
        setGeneratedCredentials({
          email: formData.email,
          password: formData.password,
        });

        if (isExistingUser) {
          showToast("Linked to existing user account.", "info");
        }

        setShowCredentialsModal(true);
      } else {
        // Editing existing client
        const clientData = {
          ...editingClient,
          ...formData,
          updatedAt: new Date().toISOString(),
        };

        // Handle password update
        let passwordUpdateResult = null;
        if (formData.password && formData.password.trim()) {
          // Update password in Firebase Authentication if user has UID
          if (editingClient.uid) {
            passwordUpdateResult = await updateUserPassword(
              editingClient.uid,
              formData.password
            );
          }

          // Update password in database
          clientData.password = formData.password;
          if (clientData.credentials) {
            clientData.credentials.password = formData.password;
          }
        } else {
          // Don't save empty password
          delete clientData.password;
        }

        // Update in Firestore
        const clientRef = doc(db, "ca_admin", editingClient.id);
        await updateDoc(clientRef, clientData);
        setClients(
          clients.map((c) => (c.id === editingClient.id ? clientData : c))
        );

        // Show appropriate success/warning message
        if (formData.password && formData.password.trim()) {
          if (passwordUpdateResult?.success) {
            showToast(
              "Firm and password updated successfully in both database and authentication!",
              "success"
            );
          } else if (passwordUpdateResult?.partialSuccess) {
            showToast(
              `Firm updated successfully. ${passwordUpdateResult.error}`,
              "warning"
            );
          } else if (passwordUpdateResult) {
            showToast(
              `Firm updated in database but password update failed: ${passwordUpdateResult.error}`,
              "warning"
            );
          } else {
            showToast("Firm updated successfully!", "success");
          }
        } else {
          showToast("Firm updated successfully!", "success");
        }
      }
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error creating/updating firm:", error);
      showToast(`Error: ${error.message}`, "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({ ...client, password: "" });
    setShowModal(true);
  };

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setShowDeleteModal(true);
    setDeleteConfirmText(""); // Reset confirmation text
  };

  // Function to verify admin user structure
  const verifyAdminUser = async () => {
    try {
      const sanitizedEmail = sanitizeEmail(user.email);
      const adminRef = doc(db, "ca_admin", sanitizedEmail);
      const snapshot = await getDoc(adminRef);

      if (!snapshot.exists() || snapshot.data()?.role !== "super_admin") {
        console.warn(
          "Admin user not properly configured, attempting to fix..."
        );

        // Create/update admin user record
        const adminData = {
          email: user.email,
          role: "super_admin",
          createdAt: snapshot.exists()
            ? snapshot.data().createdAt
            : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        await setDoc(adminRef, adminData);
        console.log("Admin user record created/updated:", adminData);

        showToast("Admin permissions verified and updated.", "info");
      }
    } catch (error) {
      console.error("Error verifying admin user:", error);
    }
  };

  const confirmDelete = async () => {
    if (
      !clientToDelete ||
      deleteConfirmText.trim().toLowerCase() !==
      clientToDelete.firmName.trim().toLowerCase()
    )
      return;

    try {
      setIsDeleting(true);

      // 1. Delete from Firestore first
      const clientRef = doc(db, "ca_admin", clientToDelete.id);
      await deleteDoc(clientRef);
      console.log("Deleted from Firestore:", clientToDelete.id);

      // 2. Then, attempt to delete from Firebase Authentication
      // We use a secondary auth instance (clientAuth) to sign in and delete without affecting the admin session
      if (clientToDelete.credentials && clientToDelete.credentials.password) {
        try {
          const { email, password } = clientToDelete.credentials;

          // Sign in as the user to be deleted using secondary auth instance
          const userCredential = await signInWithEmailAndPassword(clientAuth, email, password);

          // Delete the user from authentication
          await userCredential.user.delete();

          // Ensure we are signed out from secondary auth
          await signOut(clientAuth);

          console.log("User successfully deleted from Firebase Auth");
          showToast("Firm and login credentials deleted successfully!", "success");
        } catch (authError) {
          console.error("Auth deletion partial failure:", authError);
          // Still show success since DB deletion worked (which is the primary goal)
          showToast("Firm deleted from database successfully!", "success");
        }
      } else {
        // Fallback for older records or missing passwords
        showToast("Firm deleted successfully from database!", "success");
      }

      setClients(clients.filter((c) => c.id !== clientToDelete.id));
    } catch (error) {
      console.error("Error deleting client:", error);
      showToast(`Error deleting client: ${error.message}`, "danger");
    } finally {
      setShowDeleteModal(false);
      setClientToDelete(null);
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firmName: "",
      contactPerson: "",
      email: "",
      phone: "",
      address: "",
      password: "",
      status: "active",
    });
    setFormErrors({});
    setEditingClient(null);
    setIsSubmitting(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast("Copied to clipboard!", "success");
  };

  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  // --- Render Method ---
  return (
    <Layout>
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ zIndex: 1056 }}
      >
        <Toast
          onClose={() => setToastInfo({ ...toastInfo, show: false })}
          show={toastInfo.show}
          delay={3000}
          autohide
          bg={toastInfo.variant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastInfo.variant === "success" ? (
                <div className="d-flex align-items-center text-success">
                  <CheckCircle size={20} className="me-2" />
                  <strong className="me-auto">Success</strong>
                </div>
              ) : (
                <div className="d-flex align-items-center text-danger">
                  <XCircle size={20} className="me-2" />
                  <strong className="me-auto">Error</strong>
                </div>
              )}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toastInfo.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="d-flex justify-content-between align-items-center mb-3"> {/* Reduced mb-4 to mb-3 */}
        <h1 className="h4 fw-bold mb-0">Firm Management</h1> {/* Reduced h2 to h4 */}
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn btn-primary btn-sm d-flex align-items-center" /* Added btn-sm */
        >
          <Plus className="icon-sm me-1" />
          <span>Add New Firm</span>
        </button>
      </div>

      <div className="card mb-3 shadow-sm"> {/* Reduced mb-4 to mb-3 */}
        <div className="card-body p-2"> {/* Added p-2 */}
          <InputGroup size="sm"> {/* Added size="sm" */}
            <InputGroup.Text className="bg-white border-end-0">
              <Search className="icon-sm" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search users by firm, contact, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-start-0"
            />
          </InputGroup>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="table-responsive d-none d-lg-block table-scroll-container">
          <table className="table table-hover mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th className="fw-bold text-dark">Firm Details</th>
                <th className="fw-bold text-dark">Contact Info</th>
                <th className="fw-bold text-dark">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonRow key={index} />
                ))
              ) : filteredClients.length > 0 ? (
                filteredClients.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="avatar me-2"> {/* Reduced margin me-3 to me-2 */}
                          <Building size={16} /> {/* Reduced icon size 18 to 16 */}
                        </div>
                        <div>
                          <div className="fw-medium text-dark">{client.firmName}</div>
                          <small className="text-muted d-block" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                            {client.contactPerson}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center mb-1">
                        <Mail className="icon-sm text-muted me-2" />
                        <small>{client.email}</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <Phone className="icon-sm text-muted me-2" />
                        <small>{client.phone}</small>
                      </div>
                    </td>

                    <td>
                      <div className="btn-group" role="group">
                        <button
                          onClick={() => {
                            if (client.credentials) {
                              setGeneratedCredentials(client.credentials);
                            } else {
                              // Fallback for older records without stored credentials
                              setGeneratedCredentials({
                                email: client.email,
                                password:
                                  client.password || "Password not available",
                              });
                            }
                            setShowCredentialsModal(true);
                          }}
                          className="btn btn-outline-primary btn-sm"
                          title="View Credentials"
                        >
                          <Key className="icon-sm" />
                        </button>
                        <button
                          onClick={() => handleEdit(client)}
                          className="btn btn-outline-secondary btn-sm"
                          title="Edit"
                        >
                          <Edit className="icon-sm" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(client)}
                          className="btn btn-outline-danger btn-sm"
                          title="Delete"
                        >
                          <Trash2 className="icon-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <Building className="icon-lg text-muted mx-auto mb-3" />
                    <h5 className="text-muted">No Users Found</h5>
                    <p className="text-muted small">
                      Get started by adding your first user.
                    </p>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        resetForm();
                        setShowModal(true);
                      }}
                    >
                      <Plus className="me-1" size={16} /> Add New Firm
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-lg-none p-2">
          {/* Same logic for mobile view, just with cards */}
        </div>
      </div >

      {/* --- Modals --- */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          resetForm();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-dark">
            {editingClient ? "Edit Firm" : "Add New Firm"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }}>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark">Firm Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.firmName}
                onChange={(e) => {
                  setFormData({ ...formData, firmName: e.target.value });
                  if (formErrors.firmName) {
                    setFormErrors({ ...formErrors, firmName: "" });
                  }
                }}
                isInvalid={!!formErrors.firmName}
                placeholder="Enter firm name"
                name="firmName"
                onBlur={(e) => validateField("firmName", e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.firmName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark">Contact Person *</Form.Label>
              <Form.Control
                type="text"
                value={formData.contactPerson}
                onChange={(e) => {
                  setFormData({ ...formData, contactPerson: e.target.value });
                  if (formErrors.contactPerson) {
                    setFormErrors({ ...formErrors, contactPerson: "" });
                  }
                }}
                isInvalid={!!formErrors.contactPerson}
                placeholder="Enter contact person name"
                name="contactPerson"
                onBlur={(e) => validateField("contactPerson", e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.contactPerson}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark">Email *</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (formErrors.email) {
                    setFormErrors({ ...formErrors, email: "" });
                  }
                }}
                disabled={!!editingClient}
                isInvalid={!!formErrors.email}
                placeholder="Enter email address"
                name="email"
                onBlur={(e) => validateField("email", e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
              {editingClient ? (
                <Form.Text className="text-muted">
                  Email serves as the user ID and cannot be changed.
                </Form.Text>
              ) : (
                <Form.Text className="text-muted">
                  <strong>Important:</strong> Email will serve as the unique
                  user ID and cannot be changed after creation. Please ensure
                  it's correct.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark">Phone *</Form.Label>
              <Form.Control
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, ""); // Remove non-digits
                  if (val.length <= 10) {
                    setFormData({ ...formData, phone: val });
                    // Clear error immediately if length is valid, otherwise validateField checks onBlur
                    if (val.length === 10 && formErrors.phone) {
                      setFormErrors({ ...formErrors, phone: "" });
                    }
                  }
                }}
                isInvalid={!!formErrors.phone}
                placeholder="Enter 10-digit phone number"
                name="phone"
                onBlur={(e) => validateField("phone", e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.phone}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark">Address *</Form.Label>
              <Form.Control
                as="textarea"
                value={formData.address}
                onChange={(e) => {
                  setFormData({ ...formData, address: e.target.value });
                  if (formErrors.address) {
                    setFormErrors({ ...formErrors, address: "" });
                  }
                }}
                rows={3}
                isInvalid={!!formErrors.address}
                name="address"
                onBlur={(e) => validateField("address", e.target.value)}
                placeholder="Enter complete address"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.address}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark">Password {!editingClient && "*"}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (formErrors.password) {
                      setFormErrors({ ...formErrors, password: "" });
                    }
                  }}
                  placeholder={
                    editingClient
                      ? "Leave blank to keep unchanged"
                      : "Enter password (min. 6 characters)"
                  }
                  isInvalid={!!formErrors.password}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </InputGroup>
              <Form.Control.Feedback type="invalid">
                {formErrors.password}
              </Form.Control.Feedback>
              {editingClient && (
                <Form.Text className="text-muted">
                  Only provide a new password if you want to change it.
                </Form.Text>
              )}
              {!editingClient && (
                <Form.Text className="text-muted">
                  Password must contain at least one uppercase letter, one
                  lowercase letter, and one number.
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {editingClient ? "Updating..." : "Creating..."}
              </>
            ) : editingClient ? (
              "Update Firm"
            ) : (
              "Create Firm"
            )}
          </Button>
        </Modal.Footer>
      </Modal >

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <Trash2 className="text-danger me-2" /> Delete Firm
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This action cannot be undone. To confirm, please type{" "}
            <strong>{clientToDelete?.firmName}</strong> in the box below.
          </p>
          <Form.Control
            type="text"
            placeholder="Type firm name to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={
              deleteConfirmText.trim().toLowerCase() !==
              clientToDelete?.firmName?.trim().toLowerCase()
            }
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting..
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {
        showCredentialsModal && generatedCredentials && (
          <Modal
            show={showCredentialsModal}
            onHide={() => {
              setShowCredentialsModal(false);
              setGeneratedCredentials(null);
            }}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title className="d-flex align-items-center">
                <Key className="icon-sm me-2" /> Firm Credentials
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="alert alert-warning">
                <strong>Important:</strong> Copy and save these credentials
                securely before closing.
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Email (User ID)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={generatedCredentials.email}
                    readOnly
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => copyToClipboard(generatedCredentials.email)}
                  >
                    <Copy className="icon-sm" />
                  </Button>
                </InputGroup>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={generatedCredentials.password}
                    readOnly
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => copyToClipboard(generatedCredentials.password)}
                    disabled={
                      !generatedCredentials.password ||
                      generatedCredentials.password === "Password not available"
                    }
                  >
                    <Copy className="icon-sm" />
                  </Button>
                </InputGroup>
                {(!generatedCredentials.password ||
                  generatedCredentials.password === "Password not available") && (
                    <Form.Text className="text-muted">
                      <small>
                        Password not available. Use "Edit" to set a new password.
                      </small>
                    </Form.Text>
                  )}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                onClick={() => {
                  setShowCredentialsModal(false);
                  setGeneratedCredentials(null);
                }}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        )
      }

      {/* Manual Deletion Guide Modal */}
      <ManualDeletionGuide
        show={showManualDeletionGuide}
        onHide={() => setShowManualDeletionGuide(false)}
        userEmail={deletedUserEmail}
      />
    </Layout >
  );
};

export default ClientManagement;
